import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getToken } from '@/core/token';
import { execute } from '@/graphql/execute';
import { deletePasskeyMutation, userPasskeysQuery } from '@/queries/me.query';
import { startRegistration } from '@simplewebauthn/browser';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint, Loader2, Trash2 } from 'lucide-react';
import { FunctionComponent, useState } from 'react';
import { toast } from 'sonner';

const ENDPOINT = import.meta.env.VITE_ENDPOINT;

export const PasskeySettings: FunctionComponent = () => {
  const queryClient = useQueryClient();
  const [isRegistering, setIsRegistering] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['userPasskeys'],
    queryFn: () => execute(userPasskeysQuery),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => execute(deletePasskeyMutation, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userPasskeys'] });
      toast.success('Passkey deleted');
    },
    onError: () => toast.error('Failed to delete passkey'),
  });

  const registerPasskey = async () => {
    setIsRegistering(true);
    const toastId = toast.loading('Setting up passkey...');
    try {
      const token = getToken();
      if (!token) throw new Error('Not authenticated');

      // Get registration options
      const optionsRes = await fetch(`${ENDPOINT}/passkey/register-options`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const { options } = await optionsRes.json();

      // Start WebAuthn registration
      const credential = await startRegistration({ optionsJSON: options });

      // Verify registration
      const verifyRes = await fetch(`${ENDPOINT}/passkey/register-verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          credential,
          challenge: options.challenge,
        }),
      });

      const result = await verifyRes.json();
      if (!result.success) throw new Error(result.error);

      queryClient.invalidateQueries({ queryKey: ['userPasskeys'] });
      toast.success('Passkey registered successfully', { id: toastId });
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled', { id: toastId });
      } else {
        toast.error(error.message || 'Failed to register passkey', { id: toastId });
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const passkeys = data?.userPasskeys ?? [];

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle>Passkeys</CardTitle>
            <CardDescription>Use biometrics or a security key to sign in without a password.</CardDescription>
          </div>
          <Button onClick={registerPasskey} disabled={isRegistering}>
            {isRegistering ? <Loader2 className='size-4 animate-spin mr-2' /> : <Fingerprint className='size-4 mr-2' />}
            Add Passkey
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className='flex justify-center py-4'>
            <Loader2 className='size-5 animate-spin' />
          </div>
        ) : passkeys.length === 0 ? (
          <p className='text-sm text-muted-foreground py-2'>
            No passkeys registered. Add one to enable passwordless sign-in.
          </p>
        ) : (
          <div className='space-y-3'>
            {passkeys.map((pk) => (
              <div key={pk.id} className='flex items-center justify-between p-3 border rounded-lg'>
                <div className='flex items-center gap-3'>
                  <Fingerprint className='size-5 text-muted-foreground' />
                  <div>
                    <p className='text-sm font-medium'>{pk.name}</p>
                    <p className='text-xs text-muted-foreground'>
                      Added {new Date(pk.createdAt).toLocaleDateString()}
                      {pk.lastUsedAt && ` · Last used ${new Date(pk.lastUsedAt).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant='ghost' size='icon' disabled={deleteMutation.isPending}>
                      <Trash2 className='size-4 text-destructive' />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Passkey</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{pk.name}"? You won't be able to use it to sign in anymore.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction variant='destructive' onClick={() => deleteMutation.mutate(pk.id)}>
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
